import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Truck, FilterIcon, ArrowLeft, Weight, FileText } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { clientReceptionColumns, clientColumnsExcel } from './utils/clientReceptionColumns';
import { fetchReceptions, exportReceptions, getTotalKilos } from '@/pages/reception/services/reception.services';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';
import AppliedFilters from '@/components/AppliedFilters';

const ITEMS_PER_PAGE = 10;

const ClientRecepcionRopa = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  // Obtener datos del usuario autenticado
  const clienteName = user?.name || user?.raw?.nombre || '';
  const idLavanderia = user?.lavanderia?.id || user?.raw?.lavanderia?.id || '';

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Fecha de hoy para filtro por defecto
  const today = new Date();
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7, 0, 0, 0).toISOString();
  const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59).toISOString();

  // Filtros iniciales: por lavandería del usuario y últimos 7 días
  const [filters, setFilters] = useState({
    idLavanderia: idLavanderia,
    createdAt: {
      $gte: startOfDay,
      $lte: endOfDay,
    },
  });

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [loteFilter, setLoteFilter] = useState('');
  const [totalKilos, setTotalKilos] = useState(0);
  const [loadingTotalKilos, setLoadingTotalKilos] = useState(false);

  // Cargar total de kilos
  const loadTotalKilos = useCallback(async () => {
    setLoadingTotalKilos(true);
    try {
      const result = await getTotalKilos(filters);
      setTotalKilos(result.totalKilos || 0);
    } catch (error) {
      console.error('Error cargando el total de kilos:', error);
    } finally {
      setLoadingTotalKilos(false);
    }
  }, [filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: responseData, totalRecords } = await fetchReceptions({
        limit: ITEMS_PER_PAGE,
        page: currentPage,
        query: filters,
      });
      setData(responseData || []);
      setTotalItems(totalRecords || 0);
      loadTotalKilos();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudieron cargar las recepciones.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, toast, loadTotalKilos]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (newFilters) => {
    // Siempre mantener el filtro por lavandería
    const mergedFilters = {
      ...newFilters,
      idLavanderia: idLavanderia,
    };
    setCurrentPage(1);
    setFilters(mergedFilters);
  };

  const handleRemoveFilter = (key) => {
    // No permitir eliminar el filtro de lavandería
    if (key === 'idLavanderia') return;

    const newFilters = { ...filters };
    delete newFilters[key];

    if (key === 'numeroLote') {
      setLoteFilter('');
    }

    // Siempre mantener el filtro por lavandería
    newFilters.idLavanderia = idLavanderia;
    setFilters(newFilters);
    setCurrentPage(1);
  };

  // Data mapper para exportación Excel
  const receptionDataMapper = (item) => ({
    numeroLote: item.numeroLote || 'N/A',
    estado: item.estado || 'pendiente',
    fechaPesaje: new Date(item.createdAt).toLocaleString(),
    tipoPrenda: item.tipoPrenda?.nombre ?? 'N/A',
    pesoKg: item.pesoKg,
    kilosLimpios: item.kilosLimpios,
    totalRechazoKilos: item.totalRechazoKilos,
    arrastre: item.arrastre,
    cloro: item.cloro,
    grasa: item.grasa,
    tintas: item.tintas,
    oxido: item.oxido,
    clorhexidina: item.clorhexidina,
    pegante: item.pegante,
    azul_de_metileno: item.azul_de_metileno,
    otro: item.otro,
    programaLavado: item.programaLavado?.nombre ?? 'N/A',
    observaciones: item.observaciones ?? '',
  });

  const cols = clientReceptionColumns();

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="p-4 md:p-6 lg:p-8">
      <div className="mb-4">
        <Button asChild variant="outline">
          <Link to="/cliente-dashboard"><ArrowLeft className="mr-2 h-4 w-4" /> Volver al Portal</Link>
        </Button>
      </div>
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 p-3 rounded-lg"><Truck className="h-8 w-8 text-primary" /></div>
            <div>
              <CardTitle className="text-2xl">Recepción de Ropa</CardTitle>
              <CardDescription>Cliente: {clienteName || 'Cargando...'}</CardDescription>
            </div>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <Input
              placeholder="Buscar por lote..."
              value={loteFilter}
              onChange={(e) => setLoteFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const newFilters = { ...filters };
                  if (loteFilter) {
                    newFilters.numeroLote = loteFilter;
                  } else {
                    delete newFilters.numeroLote;
                  }
                  handleFilterChange(newFilters);
                }
              }}
              className="w-48"
            />
            <Button onClick={() => setIsFilterOpen(true)} variant="outline"><FilterIcon className="h-4 w-4 mr-2" />Filtrar</Button>
            <ExportExcelButton
              service={exportReceptions}
              filters={filters}
              columns={clientColumnsExcel}
              fileName={`Recepciones_${clienteName}`}
              dataMapper={receptionDataMapper}
            />
          </div>
        </CardHeader>
        <CardContent>
          {data && data.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              <div className="p-2.5 rounded-lg shadow-sm border border-primary/30 bg-gradient-to-r from-primary/5 to-transparent w-auto">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xs font-medium text-muted-foreground">Total de kilos</h3>
                    <p className="text-lg font-bold mt-0.5 text-primary">
                      {loadingTotalKilos ? (
                        <span className="text-sm text-muted-foreground">Calculando...</span>
                      ) : (
                        `${totalKilos.toLocaleString()} kg`
                      )}
                    </p>
                  </div>
                  <div className="bg-primary/20 p-1.5 rounded-full">
                    <Weight className="h-4 w-4 text-primary" />
                  </div>
                </div>
              </div>

              <div className="p-2.5 rounded-lg shadow-sm border border-blue-300/50 bg-gradient-to-r from-blue-50/10 to-transparent w-auto">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xs font-medium text-muted-foreground">Total registros</h3>
                    <p className="text-lg font-bold mt-0.5 text-blue-600">
                      {loading ? (
                        <span className="text-sm text-muted-foreground">Calculando...</span>
                      ) : (
                        `${totalItems.toLocaleString()}`
                      )}
                    </p>
                  </div>
                  <div className="bg-blue-100/50 p-1.5 rounded-full">
                    <FileText className="h-4 w-4 text-blue-600" />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="w-full">
            <AppliedFilters
              filters={filters}
              fields={cols.filter(c => c.isFilter)}
              onRemoveFilter={handleRemoveFilter}
              ignoreKeys={['idLavanderia']}
            />
          </div>
          <DataTable
            data={data}
            columns={cols}
            isLoading={loading}
          />
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      <AnimatePresence>
        {isFilterOpen && (
          <FilterDrawer
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            fields={cols.filter(c => c.isFilter)}
            initialFilters={filters}
            onApply={handleFilterChange}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ClientRecepcionRopa;
